import { Field, FieldProps } from "formik";
import _ from "lodash";
import { FieldInputProps } from "formik/dist/types";

import { OptionType } from "../constants";
import * as styledElements from "./FormikSelectStyles";
import FormikErrorMessage from "../FormikErrorMessage";
import { ReactNode, useState } from "react";
import FlashPCBSelect, {
  FlashPCBCreatableSelect,
} from "../../common/FlashPCBSelect";
import { MultiValue, SingleValue } from "react-select";

interface CustomSelectProps<OptionValue> extends FieldProps {
  options: OptionType<OptionValue>[];
  isLoading?: boolean;
  isMulti?: boolean;
  disabled?: boolean;
  isClearable?: boolean;
  isCreatable?: boolean;
}

function CustomSelect<OptionValue>({
  field,
  form,
  options,
  isLoading = false,
  isMulti = false,
  disabled = false,
  isClearable = false,
  isCreatable = false,
}: CustomSelectProps<OptionValue>) {
  const [createdOptions, setCreatedOptions] = useState<
    OptionType<OptionValue>[]
  >([]);
  const passedInAndCreatedOptions = [...options, ...createdOptions];

  const onChangeMulti = (newValue: MultiValue<OptionType<OptionValue>>) => {
    void form.setFieldValue(
      field.name,
      newValue.map((item: OptionType<OptionValue>) => item.value),
    );
  };
  const onChangeSingle = (option: SingleValue<OptionType<OptionValue>>) => {
    if (option === null) {
      void form.setFieldValue(field.name, null);
    } else {
      void form.setFieldValue(field.name, option.value);
    }
  };
  const onChange = (
    newValue:
      | MultiValue<OptionType<OptionValue>>
      | SingleValue<OptionType<OptionValue>>,
  ) => {
    if (isMulti) {
      onChangeMulti(newValue as MultiValue<OptionType<OptionValue>>);
    } else {
      onChangeSingle(newValue as SingleValue<OptionType<OptionValue>>);
    }
  };

  const onCreateOption = (inputValue: string) => {
    const newOption = {
      label: inputValue,
      value: inputValue,
    } as OptionType<OptionValue>;
    setCreatedOptions((prev) => [...prev, newOption]);
    if (isMulti) {
      const currentValue = field.value as OptionValue[];
      void form.setFieldValue(field.name, [
        ...currentValue,
        inputValue as OptionValue,
      ]);
    } else {
      void form.setFieldValue(field.name, inputValue as OptionValue);
    }
  };

  const getValue = () => {
    if (passedInAndCreatedOptions) {
      return isMulti
        ? (field as FieldInputProps<OptionValue[]>).value
            .map((value) =>
              passedInAndCreatedOptions.find((option) =>
                _.isEqual(option.value, value),
              ),
            )
            .filter((value) => value !== undefined)
        : passedInAndCreatedOptions.find((option) =>
            _.isEqual(option.value, field.value),
          ) || null;
    } else {
      return isMulti ? [] : null;
    }
  };

  if (isCreatable) {
    return (
      <FlashPCBCreatableSelect
        onChange={onChange}
        onCreateOption={onCreateOption}
        value={getValue()}
        options={passedInAndCreatedOptions}
        isLoading={isLoading}
        isMulti={isMulti}
        isDisabled={disabled}
        isClearable={isClearable}
      />
    );
  } else {
    return (
      <FlashPCBSelect
        onChange={onChange}
        value={getValue()}
        options={passedInAndCreatedOptions}
        isLoading={isLoading}
        isMulti={isMulti}
        isDisabled={disabled}
        isClearable={isClearable}
      />
    );
  }
}

export default function FormikSelect<OptionValue>({
  name,
  label,
  options,
  isLoading = false,
  isMulti = false,
  disabled = false,
  isClearable = false,
  isCreatable = false,
}: {
  name: string;
  label?: ReactNode;
  options: OptionType<OptionValue>[];
  isLoading?: boolean;
  isMulti?: boolean;
  disabled?: boolean;
  isClearable?: boolean;
  isCreatable?: boolean;
}) {
  return (
    <styledElements.Container>
      {label !== undefined && <label htmlFor={name}>{label}</label>}
      <Field
        component={CustomSelect}
        name={name}
        isLoading={isLoading}
        isMulti={isMulti}
        options={options}
        disabled={disabled}
        isClearable={isClearable || isMulti}
        isCreatable={isCreatable}
      />
      <FormikErrorMessage name={name} />
    </styledElements.Container>
  );
}
